/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from '@utils/i18n/i18n';

import Keyboard from '@App/components/Keyboard/Keyboard';
import Search from '@App/components/Search/Search';
import Button from '@App/components/Button/Button';
import QuestionNextButton from '@App/components/QuestionNextButton/QuestionNextButton';
import AnswerRanking from '@App/components/AnswerRanking/AnswerRanking';
import theme from '@style/theme.json';
import QuestionProgress from '@App/components/QuestionProgress/QuestionProgress';
import formVisuals from '@App/components/QuestionFormVisuals/question-form-visuals';
import { getLocalizedMuseumName, searchMuseums } from './question1-form.utils';
import './question1-form.css';

const initOptions = [];

function Question1Form() {
  const { onSelectAnswer } = useOutletContext();
  const { lang, t } = useTranslation();
  const { tabletId } = useParams();
  const isTablet = Boolean(tabletId);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [options, setOptions] = useState(initOptions);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const visual = formVisuals.question1;

  useEffect(() => {
    const matches = searchMuseums(inputValue, 20);
    setOptions(matches.map((feature) => {
      const { properties, geometry } = feature.item;
      return {
        label: getLocalizedMuseumName(feature.item, lang),
        sublabel: properties.municipality,
        value: properties.id,
        coordinates: geometry.coordinates,
      };
    }));
  }, [inputValue, lang]);

  return (
    <div
      className={`question1-form question1-form--question1${isTablet && isKeyboardOpen ? ' question1-form--keyboard-open' : ''}`}
      style={{
        '--form-accent': visual.accent,
        '--form-accent-soft': visual.accentSoft,
        '--keyboard-height': `${keyboardHeight || 196}px`,
      }}
    >
      <div className="question1-form__body">
        <QuestionProgress questionId="question1" />
        {!answerSubmitted ? (
          <>
            <h2 className={`question1-form__body__title question1-form__body__title--tablet-${tabletId}`}>
              <span>{t('arc-layer-title')}</span>
            </h2>
            <Search
              value={inputValue}
              rootElProps={{
                'data-keyboard-target': true,
                onFocus: () => setIsKeyboardOpen(true),
                onPointerDown: () => setIsKeyboardOpen(true),
              }}
              onInputChange={(event) => {
                setInputValue(event.target.value);
                setSelectedValue(null);
              }}
              onSelect={(item) => { setSelectedValue(item); }}
              options={options}
              selectedValue={selectedValue}
              placeholder={t('arc-layer-imput-placeholder')}
              resultsTitle={t('arc-layer-results-title')}
              color={Number(tabletId) === 1
                ? theme?.color?.primary?.main
                : theme?.color?.secondary?.main}
            />
            <div className="question1-form__body__footer">
              <Button
                disabled={!selectedValue}
                onClick={() => {
                  onSelectAnswer('question1', selectedValue);
                  setAnswerSubmitted(true);
                }}
              >
                {t('arc-layer-submit-button')}
              </Button>
            </div>
          </>
        ) : (
          <div className="question1-form__thank-you">
            <h2 className={`question1-form__body__title question1-form__body__title--tablet-${tabletId}`}>
              <span>{t('arc-layer-thank-you')}</span>
            </h2>
            <p className="question1-form__thank-you-text">{t('arc-layer-thank-you-text')}</p>
            <AnswerRanking questionId="question1" selectedAnswer={selectedValue} />
            <div className="question1-form__body__footer">
              <QuestionNextButton onNext={() => navigate('../question2')}>
                {t('arc-layer-next-button')}
              </QuestionNextButton>
            </div>
          </div>
        )}
      </div>
      {tabletId && (
        <Keyboard
          isOpen={isKeyboardOpen}
          onClose={() => { setIsKeyboardOpen(false); setKeyboardHeight(0); }}
          onHeightChange={setKeyboardHeight}
          value={inputValue}
          onChange={input => setInputValue(input)}
        />
      )}
    </div>
  );
}

export default Question1Form;
